import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  ElementsConsumer,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import Grid from '@material-ui/core/Grid';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from '@kareo/design-system';

import { A } from 'shared-assets/components/Elements/';
import colors, { colorScale, defaults } from 'shared-assets/lib/colors';
import Form, { InputGroup } from 'shared-assets/components/Form';
import Label from 'shared-assets/components/Elements/Label';
import StateSelect from 'shared-assets/components/StateSelect';
import Input from 'shared-assets/components/Elements/Input';

export const GRID_SPACING = 2;

export const INPUT_MAX_LENGTH = {
  address1: 256,
  address2: 256,
  cardHolder: 50,
  city: 128,
  nickname: 50,
  note: 500,
  state: 2,
  zipcode: 5,
};

const Wrapper = styled.div`
  .StripeElement {
    background-color: ${colorScale.grey_0};
    width: 100%;
    border: 1px solid ${colorScale.grey_10};
    outline: 0;
    padding: 5px 7px;
    border-radius: 3px;
    box-shadow: inset 0 1px 3px rgb(0 0 0 / 10%);
    font-family: var(--font-family, ${defaults.fontFamily});
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    font-smoothing: antialiased;
  }
  * {
    box-sizing: border-box;
  }
`;

const SaveThisCardNote = styled.div`
  font-size: 14px;
  margin-left: 23px;
`;

// https://stripe.com/docs/stripe-js/react#element-components
const options = {
  required: true,
  style: {
    base: {
      color: colors.textColorPrimary,
      fontFamily: 'monospace',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
    },
    complete: {},
    empty: {},
    invalid: {
      color: colors.error,
    },
  },
};

const SaveCardForm = props => {
  const { elements, stripe } = props;

  const [name, nameSetter] = useState(props.name);
  const [cardNickname, cardNicknameSetter] = useState(props.cardNickname);
  const [cardComments, cardCommentsSetter] = useState(props.cardComments);
  const [line1, line1Setter] = useState(props.line1);
  const [city, citySetter] = useState(props.city);
  const [postalCode, postalCodeSetter] = useState(
    props.postalCode?.slice(0, INPUT_MAX_LENGTH.zipcode),
  );

  const [usingBillingAddress, usingBillingAddressSetter] = useState(
    props.usingBillingAddress,
  );

  const [makeDefaultCard, makeDefaultCardSetter] = useState(
    props.makeDefaultCard,
  );

  const [saveCard, setSaveCard] = useState(props.saveCard);

  const [state, stateSetter] = useState(props.state || '');

  const handleSubmit = async event => {
    event.preventDefault();

    props.onSubmit(event);

    const cardElement = elements.getElement(CardNumberElement);

    const { token, error } = await stripe.createToken(cardElement, {
      address_country: 'US',
      address_zip: postalCode || '',
      address_line1: line1 || '',
      address_city: city || '',
      address_state: state || '',
      currency: 'usd',
      name,
    });

    if (token) {
      props.onAuthorize({
        ...token,
        cardNickname,
        cardComments,
        makeDefaultCard,
        saveCard,
        name,
        usingBillingAddress,
      });
    }

    if (error) {
      props.onError(error);
    }
  };

  const hasDefaultBillingAddress = !!(
    props.line1 &&
    props.city &&
    props.state &&
    props.postalCode
  );

  return (
    <Form id={props.formId} onSubmit={handleSubmit}>
      <Grid container spacing={GRID_SPACING}>
        <Grid item xs={12} md={6}>
          <Label>
            Cardholder Name
            <Input
              maxLength={INPUT_MAX_LENGTH.cardHolder}
              max-length={256}
              onChange={e => nameSetter(e.target.value)}
              placeholder="John Doe"
              required
              value={name}
            />
          </Label>
        </Grid>

        <Grid item xs={12} md={6}>
          <Label>
            Card Number
            <CardNumberElement id="card-number-element" options={options} />
          </Label>
        </Grid>

        <Grid item xs={6} md={3}>
          <Label>
            Expiration Date
            <CardExpiryElement required options={options} />
          </Label>
        </Grid>

        <Grid item xs={6} md={3}>
          <Label>
            CVC
            <CardCvcElement options={options} />
          </Label>
        </Grid>

        {hasDefaultBillingAddress && (
          <Grid item xs={12}>
            <InputGroup
              checked={usingBillingAddress}
              onChange={e => {
                usingBillingAddressSetter(e.target.checked);
                props.onUseBillingAddressChange(e);
              }}
              id="using-billing-address"
              type="checkbox"
              name="using-billing-address"
              label="Billing address is same as patient address"
            />
          </Grid>
        )}

        {!(usingBillingAddress && hasDefaultBillingAddress) && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Label>
                  Billing Address
                  <Input
                    maxLength={INPUT_MAX_LENGTH.address1}
                    onChange={e => line1Setter(e.target.value)}
                    placeholder="1234 Street Name"
                    required
                    value={line1}
                  />
                </Label>
              </Grid>

              <Grid item xs={6} md={3}>
                <Label>
                  City
                  <Input
                    maxLength={INPUT_MAX_LENGTH.city}
                    onChange={e => citySetter(e.target.value)}
                    placeholder="Springfield"
                    required
                    value={city}
                  />
                </Label>
              </Grid>

              <Grid item xs={6} md={3}>
                <Label>
                  State
                  <StateSelect
                    onChange={e => stateSetter(e.target.value)}
                    value={state}
                  />
                </Label>
              </Grid>

              <Grid item xs={4} md={2}>
                <Label>
                  Zip Code
                  <Input
                    maxLength={INPUT_MAX_LENGTH.zipcode}
                    minLength={5}
                    onChange={e => postalCodeSetter(e.target.value)}
                    placeholder="00000"
                    required
                    value={postalCode}
                  />
                </Label>
              </Grid>
            </Grid>
          </Grid>
        )}

        {props.showSaveCard && (
          <Grid item xs={12}>
            <Grid container>
              <Grid item>
                <InputGroup
                  checked={saveCard}
                  onChange={e => {
                    setSaveCard(!saveCard);
                    props.onSaveCardChange(e);
                  }}
                  id="save-card"
                  type="checkbox"
                  name="save-card"
                  label={<>Save this card on file</>}
                />
              </Grid>
              <Grid item>
                <SaveThisCardNote>
                  This requires the Credit Card Authorization Form. For Engage
                  customers, this form will be emailed to patients with valid
                  email on file via Patient Intake. All other customers can{' '}
                  <A
                    target="_blank"
                    href="https://cdn.prd.cloud.tebra.com/websitehelp/documents/payments/Credit_Card_Auth_Form_PDF.pdf"
                  >
                    download the form
                  </A>
                  .
                </SaveThisCardNote>
              </Grid>
            </Grid>
          </Grid>
        )}

        {(saveCard || !props.showSaveCard) && (
          <>
            <Grid item xs={12} sm={6}>
              <Label>
                Card Nickname (optional)
                <Input
                  maxLength={INPUT_MAX_LENGTH.nickname}
                  onChange={e => cardNicknameSetter(e.target.value)}
                  placeholder="i.e. John Doe’s HSA Card."
                  value={cardNickname}
                />
              </Label>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Label>
                Comments for practice (optional)
                <Input
                  maxLength={INPUT_MAX_LENGTH.note}
                  onChange={e => cardCommentsSetter(e.target.value)}
                  placeholder="i.e. Charge this card first."
                  value={cardComments}
                />
              </Label>
            </Grid>
          </>
        )}

        {props.showMakeDefaultCard && (
          <Grid item xs={12}>
            <InputGroup
              checked={makeDefaultCard}
              onChange={e => {
                makeDefaultCardSetter(e.target.checked);
                props.onMakeDefaultCardChange(e);
              }}
              id="make-default-card"
              type="checkbox"
              name="make-default-card"
              label={props.makeDefaultCardLabel}
            />
          </Grid>
        )}

        {(props.showSubmit || props.showCancel) && (
          <Grid item xs={12}>
            <Button
              color="vitality"
              disabled={!stripe || !elements}
              form={props.formId}
              type="submit"
            >
              {props.authorizeButtonText}
            </Button>

            {props.showCancel && (
              <Button
                color="vitality"
                variant="text"
                onClick={props.onCancel}
                style={{ marginLeft: '1rem' }}
              >
                Cancel
              </Button>
            )}
          </Grid>
        )}

        {props.showSaveCardNote && (
          <Grid item xs={12}>
            <SaveThisCardNote>
              This requires the Credit Card Authorization Form. Engage customers
              can authorize cards for use by sending the Credit Card
              Authorization Form via Patient Intake. All other customers can{' '}
              <A
                target="_blank"
                href="https://cdn.prd.cloud.tebra.com/websitehelp/documents/payments/Credit_Card_Auth_Form_PDF.pdf"
              >
                download the form
              </A>
              .
            </SaveThisCardNote>
          </Grid>
        )}
      </Grid>
    </Form>
  );
};

const InjectedForm = props => {
  return (
    <ElementsConsumer>
      {({ elements, stripe }) => (
        <Wrapper>
          <SaveCardForm {...props} elements={elements} stripe={stripe} />
        </Wrapper>
      )}
    </ElementsConsumer>
  );
};

const propTypes = {
  authorizeButtonText: PropTypes.string,
  billingAddress: PropTypes.string,
  cardComments: PropTypes.string,
  cardNickname: PropTypes.string,
  cardNumber: PropTypes.string,
  city: PropTypes.string,
  cvv: PropTypes.string,
  expires: PropTypes.string,
  formId: PropTypes.string,
  line1: PropTypes.string,
  makeDefaultCard: PropTypes.bool,
  makeDefaultCardLabel: PropTypes.string,
  name: PropTypes.string,
  onAuthorize: PropTypes.func,
  onError: PropTypes.func,
  onSubmit: PropTypes.func,
  onMakeDefaultCardChange: PropTypes.func,
  onUseBillingAddressChange: PropTypes.func,
  postalCode: PropTypes.string,
  showMakeDefaultCard: PropTypes.bool,
  showSaveCard: PropTypes.bool,
  showSubmit: PropTypes.bool,
  showCancel: PropTypes.bool,
  onCancel: PropTypes.func,
  state: PropTypes.string,
  stripeApiKey: PropTypes.string,
  usingBillingAddress: PropTypes.bool,
};

const defaultProps = {
  authorizeButtonText: 'Authorize',
  billingAddress: '',
  cardComments: '',
  cardNickname: '',
  cardNumber: '',
  city: '',
  cvv: '',
  expires: '',
  formId: 'payment-card-form',
  line1: '',
  makeDefaultCard: true,
  makeDefaultCardLabel: 'Make this my default card',
  name: '',
  onAuthorize: () => {},
  onSubmit: () => {},
  onError: () => {},
  onMakeDefaultCardChange: () => {},
  onUseBillingAddressChange: () => {},
  onSaveCardChange: () => {},
  onCancel: () => {},
  postalCode: '',
  showMakeDefaultCard: true,
  showSaveCard: false,
  showSubmit: true,
  showCancel: false,
  state: '',
  usingBillingAddress: true,
};

SaveCardForm.propTypes = {
  ...propTypes,
};

InjectedForm.propTypes = {
  ...propTypes,
};

SaveCardForm.defaultProps = {
  ...defaultProps,
};

InjectedForm.defaultProps = {
  ...defaultProps,
};

export { Elements, loadStripe };

export default InjectedForm;
